import React, { useEffect, useState } from 'react';
import {
  Card,
  CardContent,
  Grid,
  Typography,
  Divider,
  Paper,
  useTheme,
  alpha,
  Box
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { Header } from '../../../Components/Header/Header';
import { useNavigate, useParams } from 'react-router-dom';
import WSTextField, { TextType } from '../../../Components/TextBox/WSTextBox';
import WSSelectField from '../../../Components/Select/WSSelect';
import WSSCheckBoxField from '../../../Components/CheckBox/WSCheckBox';
import WSHiddenField from '../../../Components/HiddenField/WSHiddenField';
import WSButton from '../../../Components/Button/WSButton';
import { callAPI, Method } from '../../../commonFunctions/CommonFunctions';
import { useSnackbar } from '../../../Components/Snackbar/SnackBarContext';

const User = () => {
  const theme = useTheme();
  const navyBlue = "#000080";

  const params = useParams();
  const id = params?.id;
  const navigate = useNavigate();
  const { showMessage } = useSnackbar();

  const {
    register,
    handleSubmit,
    control, reset, setValue
  } = useForm();

  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(
    () => () => {
      reset();
    },
    []
  );
  useEffect(() => {
    /**
     * Edit Function
     * @param id
     *
     */
    async function getById(id: any) {
      try {
        setLoading(true)
        const response: any = await callAPI(`users/getBy`, Method.GET, {}, id);

        if (response) {
          setValue("name", response.data.name);
          setValue("email", response.data.email);
          setValue("phone", response.data.phone);
          setValue("isActive", response.data.isActive)
          setValue("isAvailable", response.data.isAvailable);
        }
      } catch (error) {
        console.error("failed to retrieve data", error);
      } finally {
        setLoading(false)
      }
    }
    if (id) {
      getById(id);
    }
  }, [id, setValue]);

  const handleFormSubmit = async (data: any) => {
    try {
      setSubmitting(true);

      // const newRequest = clearRequest(request)
      if (id) {
        const response: any = await callAPI(`users/update/${id}`, Method.PUT, data);
        if (response.message) {
          showMessage(response?.message, response.success === true ? "success" : "error");
          if (response.success === true) {
            navigate('/user/list')
          }
        }

      } else {
        const response: any = await callAPI("users/create", Method.POST, data);
        if (response.message) {
          showMessage(response?.message, response.success === true ? "success" : "error");
          if (response.success === true) {
            navigate('/user/list')
          }
        }
      }
    } catch (e: any) {
      showMessage(e?.response?.data?.message || "something went wrong", 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate('/user/list');
  };

  const roles = [
    { value: 'admin', label: 'Administrator' },
    // { value: 'manager', label: 'Manager' },
    { value: 'user', label: 'User' },
    // { value: 'viewer', label: 'Viewer' },
  ];

  return (
    <>
      <Header
        type="simple"
        isDivider={true}
        title={`${id ? 'Edit' : 'Create'} User`}
        url="/user/list"
        id={'usersListHeader'}
        isBackButton={true}
      />

      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <Card
          sx={{
            padding: 4,
            backgroundColor: "#fff",
            borderRadius: 3,
            marginTop: 3,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
            border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
            background: 'linear-gradient(135deg, #ffffff 0%, #f8fbff 100%)',
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '4px',
background: `linear-gradient(135deg, ${navyBlue} 0%, ${navyBlue} 100%)`
            }
          }}
        >
          <CardContent>
            {/* Header Section */}
            <Box sx={{ mb: 4 }}>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
background: `linear-gradient(135deg, ${navyBlue}, ${navyBlue})`,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  color: 'transparent',
                  mb: 1
                }}
              >
                {id ? 'Edit User' : 'Create New User'}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {id ? 'Update user information and permissions' : 'Add a new user to the system with appropriate roles and permissions'}
              </Typography>
            </Box>

            <Grid container spacing={3}>
              {/* Personal Information Section */}
              <Grid item xs={12}>
                <Paper
                  sx={{
                    p: 3,
                    borderRadius: 2,
                    backgroundColor: alpha(theme.palette.primary.light, 0.03),
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
background: 'linear-gradient(135deg, #f5f6ff 0%, #e8ebff 100%)'
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      mb: 3,
                      fontWeight: 600,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
color: navyBlue
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        width: 4,
                        height: 20,
backgroundColor: navyBlue,
                        borderRadius: 1
                      }}
                    />
                    Personal Information
                  </Typography>

                  <Grid container spacing={3}>
                    {/* Full Name */}
                    <Grid item xs={12} md={6}>
                      <WSTextField
                        label="Full Name"
                        {...register("name", { required: "Full name is required" })}
                        id="name"
                        name="name"
                        control={control}
                        rules={{ required: true }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                            backgroundColor: '#ffffff',
                            transition: 'all 0.2s ease',
                            '&:hover fieldset': {
                              borderColor: theme.palette.primary.main,
                              boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.1)}`,
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: theme.palette.primary.main,
                              boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.1)}`,
                            }
                          },
                          '& .MuiInputLabel-root.Mui-focused': {
                            color: theme.palette.primary.main,
                          }
                        }}
                      />
                    </Grid>

                    {/* Email Address */}
                    <Grid item xs={12} md={6}>
                      <WSTextField
                        label="Email Address"
                        {...register("email", {
                          required: "Email is required",
                          pattern: {
                            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                            message: "Invalid email address"
                          }
                        })}
                        id="email"
                        name="email"
                        control={control}

                        rules={{ required: true }}


                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                            backgroundColor: '#ffffff',
                            transition: 'all 0.2s ease',
                            '&:hover fieldset': {
                              borderColor: theme.palette.primary.main,
                              boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.1)}`,
                            }
                          }
                        }}
                      />
                    </Grid>

                    {/* Phone Number */}
                    <Grid item xs={12} md={6}>
                      <WSTextField
                        label="Phone Number"
                        {...register("phone", {
                          required: "Phone number is required",
                          pattern: {
                            value: /^[0-9]{10}$/,
                            message: "Please enter a valid 10-digit phone number"
                          }
                        })}
                        id="phone"
                        name="phone"
                        control={control}

                        rules={{ required: true }}

                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                            backgroundColor: '#ffffff',
                          }
                        }}
                      />
                    </Grid>

                    {/* Password - Only show for new users */}
                    {!id && (
                      <Grid item xs={12} md={6}>
                        <WSTextField
                          label="Password"
                          {...register("password", {
                            required: "Password is required",
                            minLength: {
                              value: 6,
                              message: "Password must be at least 6 characters"
                            }
                          })}
                          id="password"
                          name="password"
                          control={control}
                          type={TextType.Password}
                          rules={{ required: true }}

                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 2,
                              backgroundColor: '#ffffff',
                            }
                          }}
                        />
                      </Grid>
                    )}
                  </Grid>
                </Paper>
              </Grid>

              {/* Account Settings Section */}
              <Grid item xs={12}>
                <Paper
                  sx={{
                    p: 3,
                    borderRadius: 2,
                    backgroundColor: 'linear-gradient(135deg, #4A90E2 0%, #357ABD 100%)',
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
background: 'linear-gradient(135deg, #f5f6ff 0%, #e8ebff 100%)'
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      mb: 3,
                      fontWeight: 600,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                     color: navyBlue

                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        width: 4,
                        height: 20,
backgroundColor: navyBlue,
                        borderRadius: 1
                      }}
                    />
                    Account Settings
                  </Typography>

                  <Grid container spacing={3}>
                    {/* Role Selection */}
                    <Grid item xs={12} md={6}>
                      <WSSelectField
                        label="User Role"
                        name="role"
                        control={control}

                        options={roles}
                        id="role"

                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                            backgroundColor: '#ffffff',
                            transition: 'all 0.2s ease',
                            '&:hover fieldset': {
                              borderColor: theme.palette.primary.main,
                              boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.3)}`,
                            }
                          }
                        }}
                      />
                    </Grid>

                    {/* Account Status */}
                    <Grid item xs={12} md={6}>
                      <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        height: '100%',
                        pl: 1
                      }}>
                        <WSSCheckBoxField
                          id="isActive"
                          label="Active Account"
                          name="isActive"
                          rules={{ required: false }}
                          control={control}
                          defaultValue={true}
                        // sx={{
                        //   '& .MuiCheckbox-root': {
                        //     color: theme.palette.secondary.main,
                        //     '&.Mui-checked': {
                        //       color: theme.palette.secondary.main,
                        //     }
                        //   }
                        // }}
                        />
                        <Typography variant="caption" sx={{ color: 'text.secondary', ml: 1 }}>
                          User will be able to login if active
                        </Typography>
                      </Box>
                    </Grid>

                    {/* Role Description */}
                    <Grid item xs={12}>
                      <Paper
                        variant="outlined"
                        sx={{
                          p: 2,
                          borderRadius: 2,
                          backgroundColor: alpha(theme.palette.info.light, 0.05),
                          border: `1px solid ${alpha(theme.palette.info.main, 0.1)}`
                        }}
                      >
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary', mb: 1 }}>
                          Role Permissions:
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
                          • <strong>Administrator:</strong> Full system access and user management<br />
                          {/* • <strong>Manager:</strong> Can manage content and view reports<br /> */}
                          • <strong>User:</strong> Standard user with basic permissions<br />
                          {/* • <strong>Viewer:</strong> Read-only access to system data */}
                        </Typography>
                      </Paper>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>

              {/* Hidden ID Field for editing */}
              {id && (
                <Grid item xs={12} style={{ display: "none" }}>
                  <WSHiddenField name="id" control={control} />
                </Grid>
              )}

              {/* Action Buttons */}
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: 2,
                    pt: 2
                  }}
                >
                  <WSButton
                    id="cancel"
                    label="Cancel"
                    type="button"
                    size="large"
                    variant="outlined"
                    onClick={handleCancel}
                    sx={{
                      minWidth: 120,
                      borderRadius: 2,
                      borderColor: alpha(theme.palette.text.primary, 0.3),
                      '&:hover': {
                        borderColor: theme.palette.text.primary,
                        backgroundColor: alpha(theme.palette.text.primary, 0.04)
                      }
                    }}
                  />

                  <WSButton
                    id="submit"
                    label={id ? 'Update User' : 'Create User'}
                    type="submit"
                    size="large"
                    loading={submitting}
                    sx={{
                      minWidth: 160,
                      borderRadius: 2,
background: `linear-gradient(135deg, ${navyBlue} 0%, ${navyBlue} 100%)`,
boxShadow: `0 4px 15px ${alpha(theme.palette.primary.main, 0.3)}`,
                      '&:hover': {
                        boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.4)}`,
                        transform: 'translateY(-1px)'
                      },
                      transition: 'all 0.3s ease'
                    }}
                  />
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </form>
    </>
  );
};

export default User;